/*
 * File: components/admin/AdvancedSettings.js
 * Mô tả: Giao diện cho các chức năng cài đặt nâng cao và chốt sổ hàng loạt.
 * PHIÊN BẢN MỚI: Thêm cột "Phép năm mới" vào bảng kết quả.
 */
import React, { useState } from 'react';
import { apiSetLeaveExpiry, apiResetAllEmployees } from '../../api';

const ConfirmationModal = ({ title, message, onConfirm, onCancel, confirmText, confirmColor }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm">
            <h2 className="text-lg font-bold mb-4">{title}</h2>
            <p className="text-gray-600 mb-6">{message}</p>
            <div className="flex justify-end gap-4">
                <button onClick={onCancel} className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300">Hủy</button>
                <button onClick={onConfirm} className={`px-4 py-2 text-white rounded-md bg-${confirmColor}-600 hover:bg-${confirmColor}-700`}>
                    {confirmText}
                </button>
            </div>
        </div>
    </div>
);

export default function AdvancedSettings() {
    const [expiryConfig, setExpiryConfig] = useState({ year: new Date().getFullYear(), expiryDate: '' });
    const [configMessage, setConfigMessage] = useState({ type: '', text: '' });
    const [isSavingConfig, setIsSavingConfig] = useState(false);

    const [resetDate, setResetDate] = useState(new Date().toISOString().split('T')[0]);
    const [isResetting, setIsResetting] = useState(false);
    const [resetResult, setResetResult] = useState(null);
    const [resetMessage, setResetMessage] = useState({ type: '', text: '' });
    const [showConfirmModal, setShowConfirmModal] = useState(false);

    const handleConfigChange = (e) => setExpiryConfig(prev => ({ ...prev, [e.target.name]: e.target.value }));

    const handleSaveConfig = async () => {
        if (!expiryConfig.year || !expiryConfig.expiryDate) {
            setConfigMessage({ type: 'error', text: 'Vui lòng chọn năm và ngày.' });
            return;
        }
        setIsSavingConfig(true);
        setConfigMessage({ type: '', text: '' });
        try {
            const result = await apiSetLeaveExpiry(expiryConfig);
            setConfigMessage({ type: 'success', text: result.message });
        } catch (error) {
            setConfigMessage({ type: 'error', text: `Lỗi: ${error.message}` });
        } finally {
            setIsSavingConfig(false);
        }
    };

    const handleBulkReset = async () => {
        setShowConfirmModal(false);
        setIsResetting(true);
        setResetResult(null);
        setResetMessage({ type: '', text: '' });
        try {
            const payload = {
                year: new Date(resetDate).getFullYear(),
                adjustmentDate: resetDate
            };
            const result = await apiResetAllEmployees(payload);
            setResetResult(result);
            setResetMessage({ type: 'success', text: `Đã chốt sổ thành công cho ${result.length} nhân viên.` });
        } catch (error) {
            setResetMessage({ type: 'error', text: `Lỗi khi chốt sổ: ${error.message}` });
        } finally {
            setIsResetting(false);
        }
    };

    const years = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() + 1 - i);

    return (
        <div className="space-y-12">
            {showConfirmModal && (
                <ConfirmationModal
                    title="Xác nhận Chốt Sổ Hàng Loạt"
                    message={`Bạn có chắc chắn muốn chốt sổ phép cho TOÀN BỘ nhân viên tại ngày ${new Date(resetDate).toLocaleDateString('vi-VN')} không?`}
                    onConfirm={handleBulkReset}
                    onCancel={() => setShowConfirmModal(false)}
                    confirmText="Đồng ý, Chốt Sổ"
                    confirmColor="red"
                />
            )}

            {/* Phần Cài đặt Ngày Hết Hạn Phép Tồn */}
            <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Cài đặt Ngày Hết Hạn Phép Tồn</h2>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                    <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
                        <div>
                            <label htmlFor="config-year" className="block text-sm font-medium text-gray-700">Chọn năm</label>
                            <select id="config-year" name="year" value={expiryConfig.year} onChange={handleConfigChange} className="mt-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm">
                                {years.map(y => <option key={y} value={y}>{y}</option>)}
                            </select>
                        </div>
                        <div>
                            <label htmlFor="expiryDate" className="block text-sm font-medium text-gray-700">Ngày hết hạn</label>
                            <input type="date" id="expiryDate" name="expiryDate" value={expiryConfig.expiryDate} onChange={handleConfigChange} className="mt-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm" />
                        </div>
                        <div className="self-end">
                            <button onClick={handleSaveConfig} disabled={isSavingConfig} className="px-6 py-2 font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-400">
                                {isSavingConfig ? 'Đang lưu...' : 'Lưu Cài đặt'}
                            </button>
                        </div>
                    </div>
                    {configMessage.text && <p className={`mt-4 text-center text-sm ${configMessage.type === 'error' ? 'text-red-600' : 'text-green-600'}`}>{configMessage.text}</p>}
                </div>
            </div>

            {/* Phần Chốt Sổ Hàng Loạt */}
            <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Chốt Sổ Phép Toàn Bộ Nhân Viên</h2>
                <div className="border-2 border-dashed border-red-300 rounded-lg p-6">
                    <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
                        <div>
                            <label htmlFor="reset-date" className="block text-sm font-medium text-gray-700">Chọn ngày chốt sổ</label>
                            <input type="date" id="reset-date" value={resetDate} onChange={(e) => setResetDate(e.target.value)} className="mt-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm" />
                        </div>
                        <div className="self-end">
                            <button onClick={() => setShowConfirmModal(true)} disabled={isResetting || !resetDate} className="px-6 py-2 font-semibold text-white bg-red-600 rounded-md hover:bg-red-700 disabled:bg-gray-400">
                                {isResetting ? 'Đang xử lý...' : 'Chốt Sổ Toàn Bộ'}
                            </button>
                        </div>
                    </div>
                    {resetMessage.text && <p className={`mt-4 text-center text-sm ${resetMessage.type === 'error' ? 'text-red-600' : 'text-green-600'}`}>{resetMessage.text}</p>}
                    
                    {resetResult && resetResult.length > 0 && (
                        <div className="mt-6 overflow-x-auto max-h-[60vh] overflow-y-auto">
                            <h3 className="font-bold text-lg mb-2 text-center text-gray-800">Kết quả Chốt Sổ Chi Tiết</h3>
                            <table className="min-w-full bg-white border">
                                <thead className="bg-gray-100 sticky top-0 z-10">
                                    <tr>
                                        <th className="px-4 py-2 text-left text-sm font-semibold">MSNV</th>
                                        <th className="px-4 py-2 text-left text-sm font-semibold">Tên Nhân Viên</th>
                                        <th className="px-4 py-2 text-right text-sm font-semibold">Phép tồn</th>
                                        <th className="px-4 py-2 text-right text-sm font-semibold">Tổng đã dùng</th>
                                        <th className="px-4 py-2 text-right text-sm font-semibold">Đã dùng từ tồn</th>
                                        <th className="px-4 py-2 text-right text-sm font-semibold">Phép năm mới</th>
                                        <th className="px-4 py-2 text-right text-sm font-semibold">Số dư mới</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y">
                                    {resetResult.map(emp => (
                                        <tr key={emp.userId}>
                                            <td className="px-4 py-2 font-medium">{emp.userId}</td>
                                            <td className="px-4 py-2">{emp.employeeName}</td>
                                            <td className="px-4 py-2 text-right">{emp.carriedOver}</td>
                                            <td className="px-4 py-2 text-right">{emp.totalUsedToDate}</td>
                                            <td className="px-4 py-2 text-right">{emp.usedFromCarriedOver}</td>
                                            <td className="px-4 py-2 text-right">{emp.entitledThisYear}</td>
                                            <td className="px-4 py-2 text-right font-bold text-blue-700">{emp.resetBalance}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
